/**
 * The two answers, as two slots (SPEC §2.3 phase 2: "who the turn player will
 * place top and who they will place bottom").
 *
 * DESIGN.md §3.4 asked one extreme at a time, on its own screen, with a back
 * action to reach the first answer again. That is reversed. Two picks over four
 * screen states meant the predictor spent more taps navigating the wizard than
 * answering it, and "change your last pick" cost you a trip past your first one.
 * Here both answers are on the screen at once and each is its own control: the
 * slot you tap is the slot you are replacing, and the other one is untouched.
 *
 * ## The lit slot is the question
 *
 * There is no "who will Maya rank first?" line above these. The armed slot's
 * terracotta border says which question is live, and the heading is free to be
 * the question the room is actually playing. That is the deck's brief — less
 * explanation, more room for the room — applied to the one screen that had the
 * most of it.
 *
 * ## Why the geometry is constant
 *
 * Once a predictor locks in (`lock_prediction`), these stay pinned while the
 * body underneath swaps from the room's faces to who is still out. Empty and
 * filled are the same height, and every state carries a 2px border, so nothing
 * on the screen moves by a pixel when the answer lands or when the body below
 * is replaced.
 */
import type { ReactNode } from 'react';
import { Eyebrow } from '../ui.js';
import { PlayerAvatar, type LicensePlayer } from '../LicenseCard.js';

/** Which end of the turn player's ranking an answer is for. */
export type PredictionSlot = 'first' | 'last';

export interface PredictionSlotsProps {
  /** Who this player thinks the turn player will put at the top. */
  first: LicensePlayer | null;
  /** …and at the bottom. */
  last: LicensePlayer | null;
  /**
   * The slot the next face tap fills, lit terracotta. `null` once both answers
   * are in and nothing is being replaced — the pair is complete and the screen
   * is waiting on Submit, not on another tap.
   */
  armed: PredictionSlot | null;
  /** Tapping a slot re-arms it. Never fires while `locked`. */
  onArm?: (slot: PredictionSlot) => void;
  /**
   * This player has sent `lock_prediction`, or the room's hard lock has fired.
   * Either way the answers are final and the slots stop being controls.
   */
  locked?: boolean;
  className?: string;
}

export function PredictionSlots({
  first,
  last,
  armed,
  onArm,
  locked = false,
  className = '',
}: PredictionSlotsProps) {
  return (
    <div data-testid="prediction-slots" className={`grid shrink-0 grid-cols-2 gap-2.5 ${className}`}>
      <Slot
        slot="first"
        label="First"
        player={first}
        armed={armed === 'first'}
        locked={locked}
        onArm={onArm}
      />
      <Slot
        slot="last"
        label="Last"
        player={last}
        armed={armed === 'last'}
        locked={locked}
        onArm={onArm}
      />
    </div>
  );
}

function Slot({
  slot,
  label,
  player,
  armed,
  locked,
  onArm,
}: {
  slot: PredictionSlot;
  label: string;
  player: LicensePlayer | null;
  armed: boolean;
  locked: boolean;
  onArm?: (slot: PredictionSlot) => void;
}) {
  const interactive = !locked && onArm !== undefined;
  /*
    Armed and locked are both a terracotta edge, and they never co-occur: a
    locked slot cannot be re-armed, so the border only ever means one thing on
    the screen you are looking at. Everything else is the hairline.

    An EMPTY locked slot is the exception, and it keeps the hairline. Terracotta
    is this screen's mark for an answer that is going to be scored; drawing it
    around the one slot that has no answer told the player their missing pick was
    safely in, at the exact moment the strip above went black to say the opposite.
  */
  const lit = armed || (locked && player !== null);
  const body: ReactNode = player ? (
    <>
      <PlayerAvatar player={player} size="h-11 w-11" shape="square" />
      {/* Set exactly like a grid tile's name — uppercase, letterspaced, 12px.
          DESIGN §3.4 rests on the slot and the tile being the same object, and
          a slot reading "Omar" directly above a tile reading "OMAR" is the one
          detail that gives that away. */}
      <span className="w-full truncate text-center text-[0.75rem] font-semibold tracking-[0.06em] text-ink uppercase">
        {player.name}
      </span>
    </>
  ) : locked ? (
    // Past the guillotine there is nothing to tap, so the placeholder stops
    // giving an instruction and states the cost instead (SPEC §2.3 phase 3 —
    // a missing pick earns nothing).
    <span className="text-[0.8125rem] font-semibold text-ink-soft">No pick · scores 0</span>
  ) : (
    <span className="text-[0.8125rem] font-semibold text-ink-soft/45">Tap a face</span>
  );

  const content = (
    <>
      {/* The armed slot's label goes to full ink: it is the live question, and
          green is this app's colour for a kicker, not for the thing being
          asked. */}
      <Eyebrow as="span" tone={armed ? 'ink' : 'green'} className="block">
        {label}
      </Eyebrow>
      <span className="flex flex-1 flex-col items-center justify-center gap-1.5">{body}</span>
    </>
  );

  const shell = [
    // 100px whether it holds a face or a placeholder — see the header note on
    // why this shape never changes size.
    'flex h-25 flex-col gap-1 rounded-card border-2 px-2.5 py-2 text-left transition',
    lit ? 'border-terracotta' : 'border-line',
    player || armed ? 'bg-cream' : 'bg-cream-deep',
  ].join(' ');

  if (!interactive) {
    return (
      <div
        data-testid={`prediction-slot-${slot}`}
        data-armed={armed}
        data-locked={locked}
        className={shell}
      >
        {content}
      </div>
    );
  }

  return (
    // `role="button"` rather than a real <button>: the slot's content is flow
    // content, which is invalid inside a <button>. Keyboard activation is wired
    // by hand to match, the same way the history rows do it.
    <div
      role="button"
      tabIndex={0}
      aria-pressed={armed}
      aria-label={player ? `${label} · ${player.name}, change` : `${label}, empty`}
      data-testid={`prediction-slot-${slot}`}
      data-armed={armed}
      data-locked={locked}
      onClick={() => onArm(slot)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onArm(slot);
        }
      }}
      className={`${shell} cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green active:scale-[0.99]`}
    >
      {content}
    </div>
  );
}
